/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  EstateV2Filter,
  SearchSortField,
  SearchSortOrder,
  useResourcesNewQuery,
} from "~/generated/graphql";
import { useEstateSort } from "./useEstateSort";
import { useFilteredQueryState } from "./useQueryState";

export function convertTags(tags?: string[]) {
  return tags?.map((tag) => {
    const keyValue = tag.split(",");
    return { key: keyValue[0] ?? "", values: [keyValue[1] ?? ""] };
  });
}

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

export type QueryState = EstateV2Filter &
  SortingProp & {
    page: number;
    limit: number;
    tags: string[];
    metadata: string[];
    tab: number;
    query: string;
  };

export const estateV2FilterFields: (
  | keyof EstateV2Filter
  | "field"
  | "limit"
  | "order"
  | "page"
  | "query"
)[] = [
  "provider",
  "costFrom",
  "costTo",
  "unassigned",
  "assignmentUpdatedFrom",
  "assignmentUpdatedTo",
  "waste",
  "wastePolicy",
  "co2eWaste",
  "account",
  "application",
  "environment",
  "region",
  "tags",
  "type",
  "subtype",
  "typeSubtype",
  "category",
  "page",
  "limit",
  "field",
  "order",
  "query",
  "externalIntegration",
  "enrichment",
  "enrichmentIntegrationId",
  "metadata",
  "firstSeenAtFrom",
  "firstSeenAtTo",
  "lifeSpanFrom",
  "lifeSpanTo",
  "lifeSpanExclude",
];

type Props = {
  activeTab?: number;
  defaultFilters?: EstateV2Filter;
  skipFields?: string[];
  skip?: boolean;
};

export function useEstateFetch({
  activeTab,
  defaultFilters,
  skipFields = [],
  skip = false,
}: Props) {
  const { state, updateQueryState } = useFilteredQueryState(
    estateV2FilterFields.filter(
      (filterField) => !skipFields.includes(filterField)
    )
  );

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page = 0,
    limit = 20,
    field,
    order,
    tags,
    metadata,
    tab: ignoredTab,
    query,
    ...rawFilters
  }: QueryState = state;

  const { sortField, sortDirection } = useEstateSort({
    field,
    order,
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    updateQueryState,
  });

  const filtersEstate = {
    tags: convertTags(tags),
    metadata: convertTags(metadata),
    ...rawFilters,
  };

  // Temporary using skip property because of conflict between old and new estate records view
  // (not compatible url filters, so we have to skip query when other tab is opened).
  //  After removing old tab it can be switched to useResourcesNewQuery.
  return {
    result: useResourcesNewQuery({
      skip: (activeTab !== undefined && activeTab !== 0) || skip,
      variables: {
        limit,
        page,
        sort: {
          field: sortField,
          order: sortDirection,
        },
        filter: { ...filtersEstate, ...defaultFilters },
        ...(query === undefined ? {} : { query: query.toString() }),
      },
      errorPolicy: "all",
      fetchPolicy: "no-cache",
    }),
    filter: { ...filtersEstate, ...defaultFilters },
    sort: {
      field: sortField,
      order: sortDirection,
    },
    query,
  };
}
